import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Lock, Eye, EyeOff, Download, Trash2, FileText, 
  Search, RefreshCw, Loader2, LogOut, Calendar, Mail, User
} from 'lucide-react';
import api from '../services/api';
import { toast } from 'sonner';

// PRSVR Branding
const COLORS = {
  gold: '#c9a961',
  cream: '#faf8f5',
  dark: '#1a1a1a',
  darkGold: '#a88b4a'
};

const ADMIN_PASSWORD = 'prsvr2026';

const AdminInvoices = () => {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedBrief, setSelectedBrief] = useState(null);

  useEffect(() => {
    // Check session
    const session = sessionStorage.getItem('admin_auth');
    if (session === 'true') {
      setAuthenticated(true);
      loadBriefs();
    }
  }, []);

  const handleLogin = () => {
    if (password === ADMIN_PASSWORD) {
      setAuthenticated(true);
      sessionStorage.setItem('admin_auth', 'true');
      loadBriefs();
    } else {
      toast.error('Incorrect password');
    }
  };

  const handleLogout = () => {
    setAuthenticated(false);
    sessionStorage.removeItem('admin_auth');
    setPassword('');
  };

  const loadBriefs = async () => {
    setLoading(true);
    try {
      const res = await api.get('/client-brief/admin/list');
      if (res.data.success) {
        setBriefs(res.data.briefs || []);
      }
    } catch (error) {
      toast.error('Failed to load briefs');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (briefId) => {
    if (!confirm('Are you sure you want to delete this submission?')) return;
    
    try {
      await api.delete(`/client-brief/admin/${briefId}`);
      toast.success('Brief deleted');
      loadBriefs();
      if (selectedBrief?.id === briefId) setSelectedBrief(null);
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDownloadPDF = async (briefId, clientName) => {
    try {
      const res = await api.get(`/client-brief/${briefId}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `PRSVR_Brief_${clientName.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Date', 'Client Name', 'Company', 'Email', 'Phone', 'Project Type', 'Budget', 'Timeline'];
    const rows = briefs.map(b => [
      new Date(b.created_at).toLocaleDateString(),
      b.clientName,
      b.companyName || '',
      b.email,
      b.phone || '',
      b.projectType || '',
      b.budget || '',
      b.timeline || ''
    ]);
    
    const csvContent = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `PRSVR_Briefs_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const filteredBriefs = briefs.filter(b => 
    b.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.companyName?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Login Screen
  if (!authenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: COLORS.cream }}>
        <div className="w-full max-w-md p-8">
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 mx-auto mb-4" style={{ color: COLORS.gold }} />
            <h1 className="text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
              Admin Access
            </h1>
            <p className="mt-2" style={{ color: COLORS.darkGold }}>PRSVR Client Briefs</p>
          </div>

          <div className="space-y-4">
            <div className="relative">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleLogin()}
                placeholder="Enter password"
                className="border-2 rounded-lg py-4 pr-12"
                style={{ borderColor: COLORS.gold }}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2"
                style={{ color: COLORS.darkGold }}
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            <Button 
              onClick={handleLogin}
              className="w-full py-5 font-bold uppercase tracking-wider rounded-lg"
              style={{ backgroundColor: COLORS.gold, color: COLORS.cream }}
            >
              Access Dashboard
            </Button>
          </div>
        </div>

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Montserrat:wght@400;500;600;700&display=swap');
        `}</style>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen" style={{ backgroundColor: COLORS.cream, fontFamily: "'Montserrat', sans-serif" }}>
      {/* Header */}
      <header className="py-4 px-6 border-b" style={{ borderColor: COLORS.gold, backgroundColor: 'white' }}>
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
              PRSVR Admin
            </h1>
            <p className="text-sm" style={{ color: COLORS.darkGold }}>Client Brief Dashboard</p>
          </div>
          <div className="flex items-center gap-4">
            <Button 
              onClick={handleExportCSV}
              variant="outline"
              className="font-semibold"
              style={{ borderColor: COLORS.gold, color: COLORS.gold }}
            >
              <Download className="w-4 h-4 mr-2" /> Export CSV
            </Button>
            <Button 
              onClick={handleLogout}
              variant="ghost"
              style={{ color: COLORS.darkGold }}
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats & Search */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-6">
            <div className="text-center px-6 py-3 rounded-lg" style={{ backgroundColor: 'white', border: `1px solid ${COLORS.gold}` }}>
              <p className="text-3xl font-bold" style={{ color: COLORS.gold }}>{briefs.length}</p>
              <p className="text-xs uppercase tracking-wider" style={{ color: COLORS.darkGold }}>Total Briefs</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: COLORS.darkGold }} />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search clients..."
                className="pl-10 border-2 rounded-lg"
                style={{ borderColor: COLORS.gold, width: '250px' }}
              />
            </div>
            <Button 
              onClick={loadBriefs}
              variant="outline"
              style={{ borderColor: COLORS.gold, color: COLORS.gold }}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Briefs List */}
          <div className="lg:col-span-2 space-y-4">
            {loading ? (
              <div className="text-center py-12">
                <Loader2 className="w-8 h-8 animate-spin mx-auto" style={{ color: COLORS.gold }} />
              </div>
            ) : filteredBriefs.length === 0 ? (
              <div className="text-center py-12 rounded-lg" style={{ backgroundColor: 'white', border: `1px solid ${COLORS.gold}` }}>
                <FileText className="w-12 h-12 mx-auto mb-4" style={{ color: COLORS.gold }} />
                <p style={{ color: COLORS.darkGold }}>No submissions yet</p>
              </div>
            ) : (
              filteredBriefs.map((brief) => (
                <div 
                  key={brief.id}
                  onClick={() => setSelectedBrief(brief)}
                  className={`p-5 rounded-lg cursor-pointer transition-all ${selectedBrief?.id === brief.id ? 'ring-2' : ''}`}
                  style={{ 
                    backgroundColor: 'white', 
                    border: `1px solid ${COLORS.gold}`,
                    ringColor: COLORS.gold
                  }}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="font-bold text-lg" style={{ color: COLORS.dark }}>{brief.clientName}</h3>
                      {brief.companyName && (
                        <p className="text-sm" style={{ color: COLORS.darkGold }}>{brief.companyName}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-sm" style={{ color: COLORS.darkGold }}>
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {brief.email}
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" /> {new Date(brief.created_at).toLocaleDateString()}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleDownloadPDF(brief.id, brief.clientName); }}
                        variant="ghost"
                        size="sm"
                        style={{ color: COLORS.gold }}
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={(e) => { e.stopPropagation(); handleDelete(brief.id); }}
                        variant="ghost"
                        size="sm"
                        style={{ color: '#dc2626' }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {brief.projectType && (
                    <span 
                      className="inline-block mt-3 px-3 py-1 rounded-full text-xs font-semibold uppercase"
                      style={{ backgroundColor: `${COLORS.gold}20`, color: COLORS.gold }}
                    >
                      {brief.projectType.replace('_', ' ')}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>

          {/* Detail Panel */}
          <div className="lg:col-span-1">
            {selectedBrief ? (
              <div className="p-6 rounded-lg sticky top-6" style={{ backgroundColor: 'white', border: `2px solid ${COLORS.gold}` }}>
                <h3 className="text-xl font-bold mb-4" style={{ fontFamily: "'Cormorant Garamond', serif", color: COLORS.dark }}>
                  Brief Details
                </h3>

                <div className="space-y-4 text-sm">
                  <div>
                    <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Client</p>
                    <p style={{ color: COLORS.dark }}>{selectedBrief.clientName}</p>
                  </div>

                  {selectedBrief.companyName && (
                    <div>
                      <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Company</p>
                      <p style={{ color: COLORS.dark }}>{selectedBrief.companyName}</p>
                    </div>
                  )}

                  <div>
                    <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Email</p>
                    <p style={{ color: COLORS.dark }}>{selectedBrief.email}</p>
                  </div>

                  {selectedBrief.phone && (
                    <div>
                      <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Phone</p>
                      <p style={{ color: COLORS.dark }}>{selectedBrief.phone}</p>
                    </div>
                  )}

                  {selectedBrief.projectType && (
                    <div>
                      <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Project Type</p>
                      <p style={{ color: COLORS.dark }}>{selectedBrief.projectType.replace('_', ' ')}</p>
                    </div>
                  )}

                  {selectedBrief.budget && (
                    <div>
                      <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Budget</p>
                      <p style={{ color: COLORS.dark }}>{selectedBrief.budget.replace('_', ' ')}</p>
                    </div>
                  )}

                  {selectedBrief.timeline && (
                    <div>
                      <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Timeline</p>
                      <p style={{ color: COLORS.dark }}>{selectedBrief.timeline.replace('_', ' ')}</p>
                    </div>
                  )}

                  <div>
                    <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Product Vision</p>
                    <p className="whitespace-pre-wrap" style={{ color: COLORS.dark }}>{selectedBrief.productVision}</p>
                  </div>

                  {selectedBrief.additionalNotes && (
                    <div>
                      <p className="font-semibold uppercase tracking-wider text-xs" style={{ color: COLORS.darkGold }}>Notes</p>
                      <p className="whitespace-pre-wrap" style={{ color: COLORS.dark }}>{selectedBrief.additionalNotes}</p>
                    </div>
                  )}
                </div>

                <Button 
                  onClick={() => handleDownloadPDF(selectedBrief.id, selectedBrief.clientName)}
                  className="w-full mt-6 py-4 font-bold uppercase tracking-wider rounded-lg"
                  style={{ backgroundColor: COLORS.gold, color: COLORS.cream }}
                >
                  <Download className="w-4 h-4 mr-2" /> Download PDF
                </Button>
              </div>
            ) : (
              <div className="p-6 rounded-lg text-center" style={{ backgroundColor: 'white', border: `1px dashed ${COLORS.gold}` }}>
                <User className="w-12 h-12 mx-auto mb-4" style={{ color: COLORS.gold }} />
                <p style={{ color: COLORS.darkGold }}>Select a brief to view details</p>
              </div>
            )}
          </div>
        </div>
      </main>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=Montserrat:wght@400;500;600;700&display=swap');
      `}</style>
    </div>
  );
};

export default AdminInvoices;
